import { useEffect, useState } from "react";
import { CloudArrowUp, CheckCircle, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { queueCount, flushNow } from "@/lib/offlineQueue";

export default function QueueBadge({ onFlushed }) {
  const [count, setCount] = useState(0);
  const [flushing, setFlushing] = useState(false);

  const refresh = async () => setCount(await queueCount());

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 4000);
    const onOnline = () => { flushNow(); };
    const onMsg = (e) => {
      if (e.data?.type === "queue-flushed") {
        if (e.data.success > 0) {
          toast.success(`Synced ${e.data.success} offline action${e.data.success === 1 ? "" : "s"}`);
          onFlushed?.();
        }
        refresh();
        setFlushing(false);
      }
    };
    window.addEventListener("online", onOnline);
    navigator.serviceWorker?.addEventListener("message", onMsg);
    return () => {
      clearInterval(id);
      window.removeEventListener("online", onOnline);
      navigator.serviceWorker?.removeEventListener("message", onMsg);
    };
  }, [onFlushed]);

  if (count === 0 && !flushing) return null;

  const handleFlush = async () => {
    setFlushing(true);
    await flushNow();
  };

  return (
    <button
      onClick={handleFlush}
      disabled={flushing || !navigator.onLine}
      data-testid="queue-badge"
      title={navigator.onLine ? "Sync queued offline actions" : "Will sync when back online"}
      className="px-2 py-1 bg-[#4682b4]/20 border border-[#4682b4] text-[#4682b4] text-[10px] uppercase tracking-[0.2em] font-bold flex items-center gap-1 hover:bg-[#4682b4]/30 disabled:opacity-60"
    >
      {flushing
        ? <ArrowsClockwise size={10} className="animate-spin" />
        : count > 0
        ? <CloudArrowUp size={10} weight="bold" />
        : <CheckCircle size={10} weight="bold" />}
      {count > 0 ? `${count} queued` : "synced"}
    </button>
  );
}
